import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Checkbox,
} from '@mui/material';
import { RiCloseLine, RiArrowRightSLine, RiSearchLine } from '@remixicon/react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMenuItems } from '../../redux/slices/assetsSlice';
import { addElement } from '../../redux/slices/canvasSlice';
import { colors, borderRadius, naturalScale } from '../../../theme/tokens';

// Extract option/size columns from menu items (V8 format)
export const extractSizesFromMenuV8 = (subcategories) => {
  const sizeMap = new Map(); // normalizedSize -> { id, name }
  let sizeIdCounter = 1;
  let lastValidSize = null;
  const updatedCategories = [];

  const normalize = (str) =>
    str?.toLowerCase().replace(/\s+/g, ' ').trim() || '';

  const standardizeSize = (str) =>
    normalize(
      str
        .replace(/["”]/g, ' inch') // keep 10" as 10 inch
        .replace(/\b(pounder|burger)\b/g, 'pound')
        .replace(/\b(inches|inch|in)\b/g, 'inch'
        )
    );

  const getMatchingSizeFromMap = (normalizedNewSize) => {
    for (const [existingNorm, { id, name }] of sizeMap.entries()) {
      if (
        normalizedNewSize.includes(existingNorm) ||
        existingNorm.includes(normalizedNewSize)
      ) {
        return { id, name };
      }
    }
    return null;
  };

  const extractSizeFromItem = (itemName, subcatName) => {
    const normalizedItem = normalize(itemName);
    const normalizedSubcat = normalize(subcatName);

    if (normalizedItem.endsWith(normalizedSubcat)) {
      const sizePart = itemName.slice(0, itemName.length - subcatName.length).trim();
      return sizePart;
    }

    // fallback: remove any shared words
    const itemWords = normalize(itemName).split(' ');
    const subcatWords = normalize(subcatName).split(' ');
    const sizeWords = itemWords.filter((word) => !subcatWords.includes(word));
    return sizeWords.join(' ').trim();
  };

  for (const subcat of Array.isArray(subcategories) ? subcategories : [subcategories]) {
    const updatedItems = [];

    for (const item of subcat.items || []) {
      const rawSize = extractSizeFromItem(item.name, subcat.name);
      const normalizedSize = standardizeSize(rawSize);
      let sizeId = null;
      let finalSizeName = null;

      if (normalizedSize) {
        const match = getMatchingSizeFromMap(normalizedSize);
        if (match) {
          sizeId = match.id;
          finalSizeName = match.name;
        } else {
          const id = `size-${sizeIdCounter++}`;
          sizeMap.set(normalizedSize, { id, name: rawSize });
          sizeId = id;
          finalSizeName = rawSize;
        }
        lastValidSize = { sizeId, size: finalSizeName };
      } else if (lastValidSize) {
        sizeId = lastValidSize.sizeId;
        finalSizeName = lastValidSize.size;
      }

      updatedItems.push({
        ...item,
        size: finalSizeName ?? null,
        sizeId: sizeId ?? null,
      });
    }

    updatedCategories.push({
      ...subcat,
      items: updatedItems,
    });
  }

  const sizes = Array.from(sizeMap.values());
  return {
    sizes,
    categories: updatedCategories,
  };
};

/**
 * Menu Modal - Shows menu items from API
 * Slide-in modal beside the left toolbar
 * Displays hierarchical structure: Categories -> Subcategories -> Items
 */
const MenuModal = ({ onClose, storeId, apiToken, syncMenu = 1, menuVersion = 'v1' }) => {
  const dispatch = useDispatch();
  const { menuItems, menuItemsLoading } = useSelector((state) => state.assets);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('matrix'); // 'matrix' | 'listed'
  const [lastStoreId, setLastStoreId] = useState(null);

  // Reset selection state when storeId changes
  useEffect(() => {
    if (storeId !== lastStoreId) {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedSubcategoryIds(new Set());
      setSelectedItems(new Set());
      setSearchQuery('');
      setLastStoreId(storeId);
    }
  }, [storeId, lastStoreId]);

  useEffect(() => {
    if (storeId && apiToken) {
      // Fetch menu items whenever storeId or apiToken changes
      // This ensures menu updates when branch changes
      dispatch(fetchMenuItems({ store_id: storeId, api_token: apiToken, syncMenu, menuVersion }));
    }
  }, [dispatch, storeId, apiToken, syncMenu, menuVersion]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    setSelectedSubcategoryIds(new Set());
    setSelectedItems(new Set());
  };

  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);
    // When drilling into a single subcategory, clear bulk selection
    setSelectedSubcategoryIds(new Set());
    setSelectedItems(new Set());
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedSubcategoryIds(new Set());
    setSelectedItems(new Set());
  };

  const handleBackToSubcategories = () => {
    setSelectedSubcategory(null);
    setSelectedSubcategoryIds(new Set());
    setSelectedItems(new Set());
  };

  const handleItemToggle = (itemId) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleAddSelectedItems = () => {
    if (!selectedSubcategory || !selectedSubcategory.items) return;

    const selectedItemsArray = selectedSubcategory.items.filter((item) =>
      selectedItems.has(item.id)
    );

    if (selectedItemsArray.length === 0) return;

    if (viewMode === 'matrix') {
      // Matrix mode: pass subcategory with enriched items
      const matrixMeta = extractSizesFromMenuV8(selectedSubcategory);

      dispatch(
        addElement({
          type: 'menuItem',
          properties: {
            subcategories: [selectedSubcategory],
            layout: viewMode,
            sizes: matrixMeta.sizes || [],
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            nameFontSize: '24px',
            descriptionFontSize: '16px',
            priceFontSize: '20px',
            priceColor: '#C61F2F',
          },
          size: { width: 350, height: 400 },
        })
      );
    } else {
      // List mode: pass items array
      dispatch(
        addElement({
          type: 'menuItem',
          properties: {
            items: selectedItemsArray,
            layout: viewMode,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            nameFontSize: '24px',
            descriptionFontSize: '16px',
            priceFontSize: '20px',
            priceColor: '#C61F2F',
          },
          size: { width: 350, height: 400 },
        })
      );
    }
    setSelectedItems(new Set());
  };

  // Filter categories/subcategories/items based on search
  const getFilteredCategories = () => {
    if (!searchQuery) return menuItems;
    const query = searchQuery.toLowerCase();
    return menuItems.filter((cat) =>
      cat.name.toLowerCase().includes(query) ||
      cat.subs?.some((sub) =>
        sub.name.toLowerCase().includes(query) ||
        sub.items?.some((item) => item.name.toLowerCase().includes(query))
      )
    );
  };

  const getFilteredSubcategories = () => {
    if (!selectedCategory) return [];
    if (!searchQuery) return selectedCategory.subs || [];
    const query = searchQuery.toLowerCase();
    return (selectedCategory.subs || []).filter(
      (sub) =>
        sub.name.toLowerCase().includes(query) ||
        sub.items?.some((item) => item.name.toLowerCase().includes(query))
    );
  };

  const getFilteredItems = () => {
    if (!selectedSubcategory) return [];
    if (!searchQuery) return selectedSubcategory.items || [];
    const query = searchQuery.toLowerCase();
    return (selectedSubcategory.items || []).filter((item) =>
      item.name.toLowerCase().includes(query)
    );
  };

  const filteredCategories = getFilteredCategories();
  const filteredSubcategories = getFilteredSubcategories();
  const filteredItems = getFilteredItems();

  const previewItems = filteredItems.slice(0, 4);
  const matrixMeta =
    viewMode === 'matrix' && selectedSubcategory
      ? extractSizesFromMenuV8(selectedSubcategory)
      : { sizes: [], categories: [] };
  const matrixSizes = (matrixMeta.sizes || []).slice(0, 3);

  // Bulk select for subcategories
  const allSubcategoriesSelected =
    filteredSubcategories.length > 0 &&
    filteredSubcategories.every((sub) => selectedSubcategoryIds.has(sub.id));

  const handleSubcategoryCheckboxToggle = (subcategory) => {
    setSelectedSubcategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(subcategory.id)) {
        next.delete(subcategory.id);
      } else {
        next.add(subcategory.id);
      }
      return next;
    });
  };

  const handleToggleSelectAllSubcategories = () => {
    setSelectedSubcategoryIds((prev) => {
      if (allSubcategoriesSelected) {
        return new Set();
      }
      const next = new Set(prev);
      filteredSubcategories.forEach((sub) => {
        next.add(sub.id);
      });
      return next;
    });
  };

  const handleAddSelectedSubcategoryItems = () => {
    if (!selectedCategory || !selectedCategory.subs) return;

    const allSelectedItems = [];
    const subcategoriesWithItems = [];

    selectedCategory.subs.forEach((sub) => {
      if (!selectedSubcategoryIds.has(sub.id)) return;
      subcategoriesWithItems.push(sub);
      (sub.items || []).forEach((item) => {
        allSelectedItems.push(item);
      });
    });

    if (subcategoriesWithItems.length === 0) return;

    if (viewMode === 'matrix') {
      // Matrix mode: pass subcategories with enriched items
      const matrixMeta = extractSizesFromMenuV8(subcategoriesWithItems);

      dispatch(
        addElement({
          type: 'menuItem',
          properties: {
            subcategories: subcategoriesWithItems,
            layout: viewMode,
            sizes: matrixMeta.sizes || [],
            backgroundColor: 'none',
            nameFontSize: '24px',
            descriptionFontSize: '16px',
            priceFontSize: '20px',
            priceColor: '#C61F2F',
          },
          size: { width: 350, height: 400 },
        })
      );
    } else {
      // List mode: pass items array
      dispatch(
        addElement({
          type: 'menuItem',
          properties: {
            items: allSelectedItems,
            layout: viewMode,
            backgroundColor: 'none',
            nameFontSize: '24px',
            descriptionFontSize: '16px',
            priceFontSize: '20px',
            priceColor: '#C61F2F',
          },
          size: { width: 350, height: 400 },
        })
      );
    }

    setSelectedSubcategoryIds(new Set());
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.neutral.white,
        borderRadius: borderRadius.xxxs,
        border: `1px solid ${colors.neutral.gray200}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.neutral.gray200}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: naturalScale[5] }}>
              Menu
            </Typography>
            <Typography variant="body2" sx={{ color: naturalScale[65], mt: 0.5 }}>
              Select Menu
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: naturalScale[50] }}>
            <RiCloseLine size={20} />
          </IconButton>
        </Box>

        {/* View mode toggle */}
        <Box
          sx={{
            display: 'inline-flex',
            borderRadius: '999px',
            border: `1px solid ${colors.neutral.gray200}`,
            overflow: 'hidden',
            mb: 1.5,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setViewMode('matrix')}
            sx={{
              px: 2.5,
              py: 0.75,
              border: 'none',
              cursor: 'pointer',
              bgcolor: viewMode === 'matrix' ? colors.neutral.white : 'transparent',
              color: viewMode === 'matrix' ? naturalScale[5] : naturalScale[65],
              fontWeight: viewMode === 'matrix' ? 600 : 500,
              fontSize: 13,
            }}
          >
            Matrix
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setViewMode('listed')}
            sx={{
              px: 2.5,
              py: 0.75,
              border: 'none',
              cursor: 'pointer',
              bgcolor: viewMode === 'listed' ? colors.neutral.white : 'transparent',
              color: viewMode === 'listed' ? naturalScale[5] : naturalScale[65],
              fontWeight: viewMode === 'listed' ? 600 : 500,
              fontSize: 13,
              borderLeft: `1px solid ${colors.neutral.gray200}`,
            }}
          >
            Listed
          </Box>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search In All Items"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <RiSearchLine size={20} color={naturalScale[50]} />
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: borderRadius.xxxs,
              bgcolor: colors.neutral.white,
              '& fieldset': {
                borderColor: colors.neutral.gray200,
              },
            },
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {menuItemsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : menuItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 4 }}>
            {storeId && apiToken
              ? 'No menu items found'
              : 'Please provide store_id and api_token to load menu items'}
          </Typography>
        ) : !selectedCategory ? (
          // Show Categories
          <List sx={{ p: 0 }}>
            {filteredCategories.map((category, index) => (
              <React.Fragment key={category.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: naturalScale[150],
                      },
                    }}
                  >
                    <ListItemText
                      primary={category.name}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        color: naturalScale[5],
                      }}
                    />
                    <RiArrowRightSLine size={20} color={naturalScale[50]} />
                  </ListItemButton>
                </ListItem>
                {index < filteredCategories.length - 1 && (
                  <Divider sx={{ borderColor: colors.neutral.gray200 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        ) : !selectedSubcategory ? (
          // Show Subcategories
          <Box>
            <ListItemButton
              onClick={handleBackToCategories}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: `1px solid ${colors.neutral.gray200}`,
                '&:hover': {
                  bgcolor: naturalScale[150],
                },
              }}
            >
              <ListItemText
                primary="← Back to Categories"
                primaryTypographyProps={{
                  fontWeight: 500,
                  color: naturalScale[50],
                }}
              />
            </ListItemButton>
            <List sx={{ p: 0 }}>
              {/* Select all subcategories row */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleToggleSelectAllSubcategories}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&:hover': {
                      bgcolor: naturalScale[150],
                    },
                  }}
                >
                  <Checkbox
                    checked={allSubcategoriesSelected}
                    indeterminate={
                      !allSubcategoriesSelected && selectedSubcategoryIds.size > 0
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSelectAllSubcategories();
                    }}
                    sx={{
                      color: naturalScale[50],
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                  <ListItemText
                    primary="Select all categories"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: naturalScale[5],
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ borderColor: colors.neutral.gray200 }} />

              {filteredSubcategories.map((subcategory, index) => (
                <React.Fragment key={subcategory.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSubcategoryClick(subcategory)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: naturalScale[150],
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedSubcategoryIds.has(subcategory.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubcategoryCheckboxToggle(subcategory);
                        }}
                        sx={{
                          mr: 1,
                          color: naturalScale[50],
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                        }}
                      />
                      <ListItemText
                        primary={subcategory.name}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          color: naturalScale[5],
                        }}
                      />
                      <RiArrowRightSLine size={20} color={naturalScale[50]} />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredSubcategories.length - 1 && (
                    <Divider sx={{ borderColor: colors.neutral.gray200 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
            {selectedSubcategoryIds.size > 0 && (
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${colors.neutral.gray200}`,
                  bgcolor: naturalScale[150],
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: naturalScale[65] }}>
                  {selectedSubcategoryIds.size} categor{selectedSubcategoryIds.size === 1 ? 'y' : 'ies'} selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    component="button"
                    onClick={handleAddSelectedSubcategoryItems}
                    sx={{
                      flex: 1,
                      py: 1,
                      px: 2,
                      bgcolor: colors.primary.main,
                      color: 'white',
                      border: 'none',
                      borderRadius: borderRadius.xxxs,
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: colors.primary.dark,
                      },
                    }}
                  >
                    Add Items from Categories
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          // Show Items with Checkboxes
          <Box>
            <ListItemButton
              onClick={handleBackToSubcategories}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: `1px solid ${colors.neutral.gray200}`,
                '&:hover': {
                  bgcolor: naturalScale[150],
                },
              }}
            >
              <ListItemText
                primary="← Back to Subcategories"
                primaryTypographyProps={{
                  fontWeight: 500,
                  color: naturalScale[50],
                }}
              />
            </ListItemButton>
            {/* Layout preview */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${colors.neutral.gray200}`,
                bgcolor: colors.neutral.gray50,
              }}
            >
              {viewMode === 'matrix' ? (
                <Box>
                  {/* Header row with dynamic option titles */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, pr: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: naturalScale[5] }}
                      >
                        Item name
                      </Typography>
                    </Box>
                    {matrixSizes.map((size) => (
                      <Box
                        key={size.id}
                        sx={{ minWidth: 70, textAlign: 'right', ml: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: naturalScale[5] }}
                        >
                          {size.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Items preview */}
                  {previewItems.map((item) => (
                    <Box key={item.id} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: naturalScale[5] }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                        {matrixSizes.map((size) => (
                          <Box
                            key={`${item.id}-${size.id}`}
                            sx={{ minWidth: 70, textAlign: 'right', ml: 1 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: naturalScale[5],
                              }}
                            >
                              {item.price ? `£${item.price}` : '£0.00'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {item.description && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', mt: 0.25, color: naturalScale[65] }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box>
                  {previewItems.map((item) => (
                    <Box key={item.id} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: naturalScale[5] }}
                        >
                          {item.name}
                        </Typography>
                        {item.price && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: naturalScale[5] }}
                          >
                            £{item.price}
                          </Typography>
                        )}
                      </Box>
                      {item.description && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', mt: 0.25, color: naturalScale[65] }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <List sx={{ p: 0 }}>
              {filteredItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleItemToggle(item.id)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: naturalScale[150],
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleItemToggle(item.id)}
                        sx={{
                          color: naturalScale[50],
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                        }}
                      />
                      <ListItemText
                        primary={item.name}
                        secondary={item.description}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          color: naturalScale[5],
                        }}
                        secondaryTypographyProps={{
                          color: naturalScale[65],
                          sx: { mt: 0.5 },
                        }}
                      />
                      {item.price && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.primary.main,
                            fontWeight: 600,
                            ml: 2,
                          }}
                        >
                          ${item.price}
                        </Typography>
                      )}
                    </ListItemButton>
                  </ListItem>
                  {index < filteredItems.length - 1 && (
                    <Divider sx={{ borderColor: colors.neutral.gray200 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
            {selectedItems.size > 0 && (
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${colors.neutral.gray200}`,
                  bgcolor: naturalScale[150],
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: naturalScale[65] }}>
                  {selectedItems.size} item(s) selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    component="button"
                    onClick={handleAddSelectedItems}
                    sx={{
                      flex: 1,
                      py: 1,
                      px: 2,
                      bgcolor: colors.primary.main,
                      color: 'white',
                      border: 'none',
                      borderRadius: borderRadius.xxxs,
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: colors.primary.dark,
                      },
                    }}
                  >
                    Add Selected Items
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MenuModal;


